import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Capacitor } from '@capacitor/core';
import {
  subscribeToReviewPromptState,
  isReviewPromptEligible,
  markReviewPromptShown,
  snoozeReviewPrompt,
} from '@shared/services/reviewPromptService';
import { getActivityDayCount } from '@shared/services/activityDayService';
import { recordFeatureUseEvent } from '@shared/services/analyticsService';
import type { ReviewPromptState } from '@shared/types';
import { useAuth } from './AuthContext';
import { useRemoteConfig } from './RemoteConfigContext';
import { EnjoyingEnoughModal } from '../components/review/EnjoyingEnoughModal';
import { logError } from '@/lib/log';

/** Source of the request — for future analytics; behaviour is identical. */
type ReviewPromptSource = 'session_complete' | 'celebration_success' | 'milestone';

interface ReviewPromptContextValue {
  /**
   * Ask to show the review prompt from a positive moment. Only opens when the
   * user is eligible (native + flag + ≥ threshold activity days + not recently
   * asked) AND the screen is quiet (no goal celebration open). If a celebration
   * is open, the request is held and retried when it closes.
   */
  requestReviewPrompt: (source: ReviewPromptSource) => void;
}

const ReviewPromptContext = createContext<ReviewPromptContextValue>({
  requestReviewPrompt: () => {},
});

/** Hook for surfaces that want to nudge the review prompt at a good moment. */
export const useReviewPromptTrigger = (): ReviewPromptContextValue =>
  useContext(ReviewPromptContext);

/** Earliest plausible activity date — used as a lower bound to count all days. */
const ACTIVITY_COUNT_START = '2000-01-01';
/** Small beat so the prompt settles in after the triggering moment, not on top of it. */
const OPEN_DELAY_MS = 800;

interface ReviewPromptProviderProps {
  children: React.ReactNode;
  /** True while a goal celebration modal is showing — we never compete with it. */
  celebrationOpen?: boolean;
  /** True when the open/just-closed celebration batch was a *success* (not a missed goal). */
  celebrationSuccess?: boolean;
}

export const ReviewPromptProvider: React.FC<ReviewPromptProviderProps> = ({
  children,
  celebrationOpen = false,
  celebrationSuccess = false,
}) => {
  const { currentUser } = useAuth();
  const { appReviewPromptEnabled, appReviewMinActivityDays } = useRemoteConfig();

  const [state, setState] = useState<ReviewPromptState | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const evaluatingRef = useRef(false);
  const pendingRef = useRef(false); // a request deferred while a celebration was open
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep current persisted state in sync (eligibility + isFirstEver depend on it).
  useEffect(() => {
    if (!currentUser) {
      setState(null);
      return;
    }
    const unsubscribe = subscribeToReviewPromptState(currentUser.uid, setState);
    return () => unsubscribe();
  }, [currentUser]);

  // Clear any pending open timer on unmount.
  useEffect(() => () => {
    if (openTimerRef.current) clearTimeout(openTimerRef.current);
  }, []);

  const requestReviewPrompt = useCallback((source: ReviewPromptSource) => {
    if (!currentUser) return;
    if (!Capacitor.isNativePlatform()) return; // web has no native review API
    if (!appReviewPromptEnabled) return;
    if (isOpen || evaluatingRef.current) return;
    if (!isReviewPromptEligible(state)) return;

    // Never compete with a goal celebration — hold and retry when it closes.
    if (celebrationOpen) {
      pendingRef.current = true;
      return;
    }

    evaluatingRef.current = true;
    void (async () => {
      try {
        const dayCount = await getActivityDayCount(currentUser.uid, ACTIVITY_COUNT_START);
        if (dayCount < (appReviewMinActivityDays || 10)) return;

        pendingRef.current = false;
        const isFirstEver = state === null;
        await markReviewPromptShown(currentUser.uid, isFirstEver);
        // Analytics-readable exposure event — the denominator for the
        // positive/negative answer rates (uservars.reviewPrompt is
        // last-state-only, so rollups can't read it).
        void recordFeatureUseEvent(currentUser.uid, 'review_prompt', 'shown', {
          oncePerSession: false,
          meta: { source },
        });

        openTimerRef.current = setTimeout(() => setIsOpen(true), OPEN_DELAY_MS);
      } catch (error) {
        logError(error);
      } finally {
        evaluatingRef.current = false;
      }
    })();
  }, [currentUser, appReviewPromptEnabled, appReviewMinActivityDays, state, isOpen, celebrationOpen]);

  // Watch the celebration: capture success while open, and act when it closes —
  // riding the wave of a *successful* celebration, or retrying a deferred request.
  const prevCelebrationOpen = useRef(false);
  const celebrationWasSuccess = useRef(false);
  useEffect(() => {
    if (celebrationOpen) {
      celebrationWasSuccess.current = celebrationSuccess;
    } else if (prevCelebrationOpen.current) {
      // Celebration just closed.
      if (celebrationWasSuccess.current) {
        requestReviewPrompt('celebration_success');
      } else if (pendingRef.current) {
        requestReviewPrompt('session_complete');
      }
      celebrationWasSuccess.current = false;
    }
    prevCelebrationOpen.current = celebrationOpen;
  }, [celebrationOpen, celebrationSuccess, requestReviewPrompt]);

  const handleClose = useCallback(() => setIsOpen(false), []);

  const handleDismiss = useCallback(() => {
    setIsOpen(false);
    if (currentUser) {
      snoozeReviewPrompt(currentUser.uid).catch(logError);
      // Closed the ask step without choosing — completes the read:
      // shown = answer_positive + answer_negative + dismissed.
      void recordFeatureUseEvent(currentUser.uid, 'review_prompt', 'dismissed', {
        oncePerSession: false,
      });
    }
  }, [currentUser]);

  return (
    <ReviewPromptContext.Provider value={{ requestReviewPrompt }}>
      {children}
      {currentUser && (
        <EnjoyingEnoughModal
          isOpen={isOpen}
          uid={currentUser.uid}
          email={currentUser.email || ''}
          onClose={handleClose}
          onDismiss={handleDismiss}
        />
      )}
    </ReviewPromptContext.Provider>
  );
};
